import { ClickAwayListener } from '@byecode/ui'
import type { DataSourceAbstract, Field, FieldADTValue } from '@lighthouse/core'
import { isSafaris } from '@lighthouse/tools'
import { Modal } from '@mantine/core'
import type { UploadyProps } from '@rpldy/shared-ui'
import type { atomWithImmer } from 'jotai-immer'
import React, { useCallback, useDeferredValue, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'

import { NO_EDIT_SYSTEM_FIELD, notEditableFieldTypes } from '../../constants'
import { useAtomData, useCellEditorPosition } from '../../hooks'
import type { FetchPexelsParams, PexelsResponse } from '../../types'
import { getIsAppointField, type RecordPoolMap, selectRecordFromPool } from '../../utils'
import { FieldValueEditor } from '../FieldValueEditor'
import type { UseUploadFileSParameter } from '../UploadManage'

interface TablePopoverProps {
    field: Field
    popperAnchorElement?: HTMLDivElement
    dataSourceInfo: DataSourceAbstract
    /** recordPoolAtom 使用 Map 结构 */
    recordPoolAtom: ReturnType<typeof atomWithImmer<RecordPoolMap>>
    recordId: string
    width?: number
    scrollElement: HTMLDivElement | null
    // getUploadOptions: (fieldId: string, recordId: string) => Pick<UseUploadFileSParameter, 'info' | 'options'>
    // getRichTextUploadOptions: (fieldId: string, recordId: string) => UploadyProps
    // getVideoUploadOptions: (fieldId: string, recordId: string) => Pick<UseUploadFileSParameter, 'info' | 'options'>
    uploadOptions: Pick<UseUploadFileSParameter, 'info' | 'options'>
    richTextUploadOptions: UploadyProps
    videoUploadOptions: Pick<UseUploadFileSParameter, 'info' | 'options'>
    onCellChange?: (recordId: string, fieldValue: FieldADTValue) => Promise<boolean>
    onCellUpdate?: (recordId: string, fieldValue: FieldADTValue) => Promise<boolean>
    onClose?: () => void
    onFetchPexelsList: (params: FetchPexelsParams) => Promise<PexelsResponse>
}

const SCxDiv = styled.div``

const SCxWindowShapeEditorPopover = styled.div`
    position: fixed;
    min-width: 100px;
    min-height: 36px;
    overflow: hidden;
    display: inline-table;
    box-shadow: var(--box-shadow);
    background-color: var(--color-white);
    z-index: 200;
`

const SCxWindowShapeEditorModal = styled(Modal)``

const SCxWindowShapeEditorModalOverlay = styled.div`
    outline: none;
    overflow: hidden;
`

export const TablePopover: React.FC<TablePopoverProps> = ({
    field,
    popperAnchorElement,
    dataSourceInfo,
    recordPoolAtom,
    width = 160,
    recordId,
    scrollElement,
    uploadOptions,
    richTextUploadOptions,
    videoUploadOptions,
    onCellChange,
    onCellUpdate,
    onClose,
    onFetchPexelsList
}) => {
    const [open, setOpen] = useState(false)
    const { appId, id: dsId } = dataSourceInfo
    const { id: fieldId } = field
    // 使用 Map 的 O(1) 查找，useMemo 保持 selector 引用稳定
    const selector = useMemo(() => selectRecordFromPool(dsId, recordId), [dsId, recordId])
    const currentRecord = useDeferredValue(useAtomData(recordPoolAtom, selector))
    const fieldValue = useMemo(() => currentRecord?.content?.[fieldId]?.value, [currentRecord?.content, fieldId])
    const [currentData, setCurrentData] = useState(fieldValue)
    const isSystemField = getIsAppointField(fieldId, NO_EDIT_SYSTEM_FIELD)
    const isNoneShape = notEditableFieldTypes.has(field?.type || '')
    const isInput = ['text', 'url', 'phoneNumber', 'email', 'number'].includes(field?.type || '')
    const isPopperShape = !isNoneShape && !['notes'].includes(field?.type || '') && !isSystemField
    const isModalShape = useMemo(() => {
        return !(isPopperShape || isSystemField)
    }, [isPopperShape, isSystemField])

    const { popoverRef, handleClearAnchorPosition, position } = useCellEditorPosition({
        scrollElement,
        anchorElement: popperAnchorElement,
        open,
        onOpenChange: setOpen
    })

    useEffect(() => {
        if (popperAnchorElement && !open) {
            setOpen(true)
        }
        if (!popperAnchorElement) {
            setOpen(false)
        }
    }, [open, popperAnchorElement])

    const handleClickOutside = useCallback(
        (isActive?: boolean) => {
            setTimeout(() => {
                onClose?.()
            }, 0)
        },
        [onClose]
    )

    const handleCellChange = useCallback(
        async (recordId: string, fieldValue: FieldADTValue) => {
            setCurrentData(fieldValue.value)
            const isUpdate = await onCellChange?.(recordId, fieldValue)
            return !!isUpdate
        },
        [onCellChange]
    )

    const handleClose = useCallback(() => {
        setOpen(false)
        onClose?.()
        handleClearAnchorPosition()
    }, [handleClearAnchorPosition, onClose])

    return useMemo(() => {
        if (isPopperShape) {
            const extraStyles: React.CSSProperties = isInput
                ? {
                      border: '2px solid var(--color-main)',
                      ...position
                  }
                : {
                      border: '1px solid var(--color-gray-200)',
                      ...position
                  }
            const mouseEvent = isSafaris() ? 'onMouseUp' : 'onMouseDown'
            return (
                <SCxDiv>
                    {open &&
                        popperAnchorElement &&
                        createPortal(
                            <ClickAwayListener data-ignore-click-away mouseEvent={mouseEvent} onClickAway={handleClose}>
                                <SCxWindowShapeEditorPopover
                                    onClick={e => e.stopPropagation()}
                                    style={{ width: width - 5, ...extraStyles }}
                                    data-ignore-click-away
                                    ref={popoverRef}
                                >
                                    {field && (
                                        <FieldValueEditor
                                            uploadOptions={uploadOptions}
                                            videoUploadOptions={videoUploadOptions}
                                            dataSourceInfo={dataSourceInfo}
                                            data={currentData}
                                            currentRecord={currentRecord}
                                            width={`${width - 5}px`}
                                            field={field}
                                            enableFileDownload
                                            recordId={recordId}
                                            autoFocus
                                            scrollElement={scrollElement}
                                            onCellChange={handleCellChange}
                                            onCellUpdate={onCellUpdate}
                                            onClose={handleClickOutside}
                                            onFetchPexelsList={onFetchPexelsList}
                                            richTextUploadOptions={richTextUploadOptions}
                                        />
                                    )}
                                </SCxWindowShapeEditorPopover>
                            </ClickAwayListener>,
                            document.body
                        )}
                </SCxDiv>
            )
        }

        if (isModalShape) {
            return (
                <SCxWindowShapeEditorModal
                    centered
                    closeOnClickOutside={false}
                    padding={0}
                    radius={12}
                    size={720}
                    withinPortal
                    overlayColor="var(--color-black)"
                    overlayOpacity={0.75}
                    withCloseButton={false}
                    onKeyDown={e => e.stopPropagation()}
                    // opened={popperState.isOpen}
                    opened={open}
                    onClose={handleClickOutside}
                    closeOnEscape={false}
                    trapFocus={false}
                >
                    <SCxWindowShapeEditorModalOverlay style={{ height: '90vh' }}>
                        {field && (
                            <FieldValueEditor
                                uploadOptions={uploadOptions}
                                videoUploadOptions={videoUploadOptions}
                                dataSourceInfo={dataSourceInfo}
                                data={currentData}
                                currentRecord={currentRecord}
                                field={field}
                                recordId={recordId}
                                scrollElement={scrollElement}
                                onCellChange={onCellChange}
                                onCellUpdate={onCellUpdate}
                                onClose={handleClickOutside}
                                onFetchPexelsList={onFetchPexelsList}
                                richTextUploadOptions={richTextUploadOptions}
                            />
                        )}
                    </SCxWindowShapeEditorModalOverlay>
                </SCxWindowShapeEditorModal>
            )
        }
        return null
    }, [
        isPopperShape,
        isModalShape,
        isInput,
        position,
        open,
        scrollElement,
        popperAnchorElement,
        handleClose,
        width,
        popoverRef,
        field,
        uploadOptions,
        videoUploadOptions,
        dataSourceInfo,
        currentData,
        currentRecord,
        recordId,
        handleCellChange,
        onCellUpdate,
        handleClickOutside,
        onFetchPexelsList,
        richTextUploadOptions,
        onCellChange
    ])
}
